import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import GlobalState from '../../../_common/platform/ts/globalStates/GlobalState';
import { IAQLParameters } from '../../platform/ts/AqlUtility';
import { Dispatcher as BaseDispatcher } from '../../../_common/platform/ts/BaseDispatcher';
import { IWidget } from '../../../pdc-platform/src/ts/Interfaces';
import { ICultureValues } from './Globalization';
import { Constants } from './Constants';
import { I, check } from '../../../pdc-platform/src/ts/actions/AppActions';

import {
    BaseWidgetStore,
    IBaseWidgetStore,
    IWidgetPrintableStoreState,
    IWidgetStoreState,
    IWidgetStoreStateOptions
} from '../../../_common/platform/ts/BaseWidgetStore';

export { IBaseWidgetParameters } from '../../../pdc-platform/src/ts/Interfaces';

export interface IAttrQueryWidgetStoreState extends IWidgetStoreState {
    pendingChangeGlobalState?: boolean;
    globalStateChanged?: number;
    ageFromBackend?: number;
    datetimeOfAge?: Date;
}

export interface IAttrQueryWidgetPrintableStoreState extends IAttrQueryWidgetStoreState, IWidgetPrintableStoreState {}

export interface IAttrQueryWidgetStore extends IBaseWidgetStore {
    getAQLParameters(cultureValues?: ICultureValues): IAQLParameters;
}

export interface IUpdateFlags {
    doNotUseGlobalState?: boolean;
    doNotChangeRefreshStatusOnUpdateInitiate?: boolean;
}
export default class BaseAttrQueryWidgetStore<
        StateType extends IAttrQueryWidgetStoreState,
        ActionsType,
        DispatcherType extends BaseDispatcher
    >
    extends BaseWidgetStore<StateType, ActionsType, DispatcherType, IGlob>
    implements IAttrQueryWidgetStore
{
    constructor(
        glob: IGlob,
        dispatcher: DispatcherType,
        data: IWidget,
        options: IWidgetStoreStateOptions,
        globalState: GlobalState
    ) {
        super(glob, dispatcher, data, options, undefined, undefined, globalState);

        this._state.pendingChangeGlobalState = false;
        this._state.globalStateChanged = 1;
        this._state.datetimeOfAge = new Date();
    }

    public getAQLParameters(cultureValues?: ICultureValues) {
        const theme = this.getTheme();
        const ignoreCache = this._state.refreshChanged < 0;

        if (cultureValues == null) {
            cultureValues = this.getCultureValues();
        }

        return this.glob.AQLQueries.getAQLParametersFromTheme(theme, cultureValues, ignoreCache);
    }

    public widgetNeedsRefresh(): boolean {
        const aqlParameters = this.getAQLParameters();

        return BaseAttrQueryWidgetStore.widgetNeedsRefresh(aqlParameters, this._state);
    }

    public static widgetNeedsRefresh(aqlParameters: IAQLParameters, state: IAttrQueryWidgetStoreState): boolean {
        const maxCacheAge = aqlParameters.maxCacheAge || 0;

        if (maxCacheAge > 0 && state.isHidden) {
            return false; // no need update because Widget invisible
        }

        let ageSinceRetrieval = 0;

        const datetimeOfAge = state.datetimeOfAge;

        if (datetimeOfAge != null) {
            ageSinceRetrieval = (new Date().getTime() - datetimeOfAge.getTime()) / 1000;
        }

        const ageFromBackend = state.ageFromBackend || 0;

        if (ageFromBackend + ageSinceRetrieval >= maxCacheAge) {
            return true;
        }

        return false;
    }

    //This works wrong when the same WS is opened both in Workspace Container and in another tab.
    // private isEventForForeignWorkspace(wsid: number) {
    //     let wsInstance = this.glob.WorkspaceStore.getInstanceState(this._state.options.workspace);
    //     return wsInstance.isChildWorkspace !== true && wsid !== this._state.options.workspace;
    // }

    public changeRefreshStatus(isPlus: boolean, changeGlobalState: boolean) {
        if (isPlus != null) {
            super.changeRefreshStatusBase(isPlus);
        }

        if (changeGlobalState) {
            this._state.globalStateChanged += 1;
        }
    }

    public override widgetSetState(workspace: number, tab: number, namespace: string, state: any): boolean {
        // if (this.isEventForForeignWorkspace(wsid)) {
        //     return false;
        // }

        // This check is the same as in the super class. It is used here to do not run the code below for wrong tab.
        if (this._state.options?.tab !== tab || this._state.options?.namespace !== namespace) {
            return false;
        }

        const res = super.widgetSetState(workspace, tab, namespace, state);

        if (!res) {
            this._state.pendingChangeGlobalState = true;
            return false;
        }

        if (this._state.globalState != null && typeof this._state.globalState.setState === 'function') {
            this._state.globalState.setState(state);
        }

        this.changeRefreshStatus(true, true);
        return true;
    }

    public override updateInitiate(
        auto: boolean,
        workspace: number,
        tab: number,
        targetWidgetID: number,
        sourceWidgetID?: number
    ): boolean {
        if (!super.updateInitiate(auto, workspace, tab, targetWidgetID, sourceWidgetID)) {
            return false;
        }

        this.changeRefreshStatus(auto, false);
        return true;
    }

    public hiddenStateChanged(hidden: boolean, widget: number, tab: number, workspace: number) {
        const result = super.hiddenStateChanged(hidden, widget, tab, workspace);

        if (result) {
            if (!hidden) {
                this.changeRefreshStatus(null, this._state.pendingChangeGlobalState);
                this._state.pendingChangeGlobalState = false;
            }
        }

        return result;
    }

    public handleBufferedDataChanged(workspace: number, tab: number, runTimeWidgetID: number) {
        if (this._state.options?.tab !== tab || this._state.options?.workspace !== workspace) {
            return false;
        }

        if (this.getRunTimeWidgetID() === runTimeWidgetID) {
            return false;
        }

        this.changeRefreshStatus(false, true);
        return true;
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const action = payload.action;

        if (check<I.WORKSPACE_BUFFERED_DATA_CHANGED>(action, Constants.WORKSPACE_BUFFERED_DATA_CHANGED)) {
            if (this.handleBufferedDataChanged(action.id, action.tab, action.runTimeWidgetID)) {
                this.emitChange();
            }
        }

        return true;
    }
}
